import { Chart, registerables } from 'chart.js';
import { useEffect, useState } from 'react';
import { Line } from "react-chartjs-2";
import { getLineChartSettings, ChartData } from './soilChart.utils';

Chart.register(...registerables);

interface SoilChartProps {
  chartData: { moisture: number; timestamp: string }[];
}

// Define a type for chart settings
interface ChartSettingsType {
  data?: ChartData;
  options?: {
    responsive?: boolean;
    scales?: {
      y?: any;
      x?: any;
    };
    plugins?: {
      tooltip?: {
        callbacks?: {
          label?: (context: any) => string;
        };
      };
      legend?: {
        labels?: any;
        display?: boolean;
      };
    };
    maintainAspectRatio?: boolean;
  };
  moistureData?: { moisture: number; timestamp: string }[];
}

const addMoistureColorScale = (chartSettings: ChartSettingsType) => {
  
  return {
    ...chartSettings,
    options: {
      ...chartSettings.options,
      responsive: true,
      scales: {
        ...chartSettings.options?.scales,
        y: {
          ...chartSettings.options?.scales?.y,
          min: 0,
          max: 10,
          ticks: {
            callback: function(value: number) {
              return value;
            }
          }
        },
        x: {
          ...chartSettings.options?.scales?.x,
          ticks: {
            callback: function(value: string | number, index: number, ticks: any[]) {
              if (chartSettings.moistureData && chartSettings.moistureData[index]) {
                const date = new Date(chartSettings.moistureData[index].timestamp);
                return date.toLocaleString('en-US', {
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                });
              }
              return value;
            }
          }
        }
      },
      plugins: {
        ...chartSettings.options?.plugins,
        tooltip: {
          ...chartSettings.options?.plugins?.tooltip,
          callbacks: {
            ...chartSettings.options?.plugins?.tooltip?.callbacks,
            label: function(context: {parsed: {y: number}}) {
              // Add moisture ref (values now on 0-10 scale)
              const value = context.parsed.y;
              let status = '';
              
              if (value <= 2.0) {
                status = '(Very Dry)';
              } else if (value <= 4.0) {
                status = '(Dry)';
              } else if (value <= 8.0) {
                status = '(Moist)';
              } else {
                status = '(Wet)';
              }
              
              return `Soil Moisture: ${value} ${status}`;
            }
          }
        }
      },
      maintainAspectRatio: false
    }
  };
};

const SoilChart = ({ chartData }: SoilChartProps) => {
  const [chartSettings, setChartSettings] = useState<ChartSettingsType | null>(null);

  useEffect(() => {
    // Only consider data points that are valid (not null or undefined)
    const cleanedData = chartData.filter(cd => typeof cd.moisture === "number" && !isNaN(cd.moisture))

    try {
      const baseChartData = getLineChartSettings(cleanedData);
      
      const baseSettings: ChartSettingsType = {
        data: {
          ...baseChartData,
          datasets: [{
            ...baseChartData.datasets[0],
            data: cleanedData.map(data => data.moisture * 10),
            borderColor: "rgb(2, 89, 250)", 
            borderWidth: 2,        // Make the line thicker
            pointBackgroundColor: "#ffffff"
          }]
        },
        options: {
          responsive: true,
          scales: {
            y: {
              min: 0,
              max: 1
            }
          },
          plugins: {
            legend: {
              display: false // Hide the legend/key at the top
            }
          },
          maintainAspectRatio: false
        },
        moistureData: cleanedData
      };
      
      const enhancedSettings = addMoistureColorScale(baseSettings); 
      setChartSettings(enhancedSettings);
    } catch (error) {
      console.error("Error setting up chart:", error);
      
      const basicChartData = getLineChartSettings(cleanedData);
      setChartSettings({
        data: basicChartData,
        options: {
          maintainAspectRatio: false
        }
      });
    }
  }, [chartData]);

  // Add gradient background plugin
  useEffect(() => {
    const moistureGradientPlugin = {
      id: 'moistureGradient',
      beforeDraw: (chart: any) => {
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        
        const gradientBg = ctx.createLinearGradient(
          0, chartArea.bottom, 0, chartArea.top
        );
        
        gradientBg.addColorStop(0, 'rgba(255, 0, 0, 0.1)');  
        gradientBg.addColorStop(0.3, 'rgba(255, 230, 0, 0.1)'); 
        gradientBg.addColorStop(0.5, 'rgba(0, 128, 255, 0.1)'); 
        gradientBg.addColorStop(1, 'rgba(0, 106, 255, 0.3)');    
        
        ctx.fillStyle = gradientBg;
        ctx.fillRect(chartArea.left, chartArea.top, 
                    chartArea.right - chartArea.left, 
                    chartArea.bottom - chartArea.top);
      }
    };
    
    Chart.register(moistureGradientPlugin);
    
    return () => {
      Chart.unregister(moistureGradientPlugin);
    };
  }, []);

  if (!chartSettings) return <div>Loading chart...</div>;

  return (
    <div>
      <div style={{ height: '350px' }}>
        {chartSettings && chartSettings.data && (
          <Line 
            data={chartSettings.data} 
            options={chartSettings.options || {}} 
          />
        )}
      </div>
      
      <div className='mt-5 flex items-center justify-center flex-col'>
        <div className='w-48 h-5 rounded' style={{ 
          background: 'linear-gradient(to right,rgba(255, 0, 0, 0.4),rgba(255, 230, 0, 0.4),rgba(0, 128, 255, 0.4),rgba(0, 106, 255, 0.4))'
        }}></div>
        <div className='flex justify-between w-48 text-xs mt-1'>
          <span>1 (Dry)</span>
          <span>5</span>
          <span>10 (Wet)</span>
        </div>
      </div>
    </div>
  );
};

export default SoilChart;
